import { cn } from "./lib/cn.js";

import type { ReactNode } from "react";

/**
 * EmptyState primitive.
 *
 * Canon: docs/design-canon/01-ui/feedback-states.md R3.
 * Replaces the five hand-rolled empty-state shapes the audit found
 * across the codebase. Centred layout, optional icon + title +
 * description, optional call-to-action.
 */
export interface EmptyStateProps {
  /** Optional decorative icon (typically a small SVG component). */
  icon?: ReactNode;
  /** Primary heading line. */
  title: string;
  /** Helper paragraph below the title. */
  description?: string;
  /**
   * Action button(s) under the description. Caller passes a `<Button>`
   * (or anything else) — we don't constrain shape so secondary actions
   * are also possible.
   */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-soft-lg border-2 border-dashed border bg-surface px-6 py-12 text-center",
        className,
      )}
    >
      {icon && <div className="text-fg-subtle">{icon}</div>}
      <h3 className="text-base font-semibold text-fg">{title}</h3>
      {description && <p className="max-w-md text-sm text-fg-subtle">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
